import React, { useMemo, memo } from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TableBody,
} from '@mui/material';

export interface VirtualizedColumn {
  id: string;
  label: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string;
}

interface VirtualizedTableProps {
  columns: VirtualizedColumn[];
  data: any[];
  rowHeight?: number;
  height?: number;
  onRowClick?: (row: any) => void;
}

/**
 * VirtualizedTable Component
 * 
 * A table component optimized for large datasets using windowing technique.
 * For datasets < 100 rows, consider using the regular Table component.
 * 
 * Performance optimizations:
 * - Only renders visible rows + overscan buffer
 * - Memoized calculations to prevent unnecessary re-renders
 * - Sticky header for better UX
 * - Row height caching
 */
export const VirtualizedTable: React.FC<VirtualizedTableProps> = memo(({
  columns,
  data,
  rowHeight = 52,
  height = 600,
  onRowClick,
}) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  
  // Calculate visible range (memoized)
  const { visibleData, offsetY, startIndex, endIndex } = useMemo(() => {
    const visibleRowCount = Math.ceil(height / rowHeight);
    const overscan = 5; // Render extra rows for smooth scrolling
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const end = Math.min(data.length, start + visibleRowCount + overscan * 2);
    
    return {
      visibleData: data.slice(start, end),
      offsetY: start * rowHeight,
      startIndex: start,
      endIndex: end,
    };
  }, [scrollTop, height, rowHeight, data]);
  
  const totalHeight = data.length * rowHeight;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      setScrollTop(event.currentTarget.scrollTop);
    });
  };

  // Memoized column styles
  const columnStyles = useMemo(() => {
    return columns.map((column) => ({
      width: column.width || `${100 / columns.length}%`,
      fontWeight: 'bold' as const,
      align: column.align || 'left' as const,
    }));
  }, [columns]);

  return (
    <TableContainer 
      component={Paper}
      onScroll={handleScroll}
      sx={{ 
        height,
        overflow: 'auto',
      }}
    >
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            {columnStyles.map((style, index) => (
              <TableCell
                key={columns[index].id}
                align={style.align}
                style={{
                  width: style.width,
                  fontWeight: style.fontWeight,
                }}
              >
                {columns[index].label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Spacer for rows before visible range */}
          {startIndex > 0 && (
            <TableRow style={{ height: offsetY }}>
              <TableCell colSpan={columns.length} style={{ padding: 0, border: 'none' }} />
            </TableRow>
          )}
          
          {/* Visible rows - memoized row component */}
          {visibleData.map((row, index) => (
            <VirtualizedTableRow
              key={startIndex + index}
              row={row}
              columns={columns}
              columnStyles={columnStyles}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              rowIndex={startIndex + index}
            />
          ))}
          
          {/* Spacer for rows after visible range */}
          {endIndex < data.length && (
            <TableRow style={{ height: totalHeight - (endIndex * rowHeight) }}>
              <TableCell colSpan={columns.length} style={{ padding: 0, border: 'none' }} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

// Memoized row component to prevent unnecessary re-renders
const VirtualizedTableRow = memo(({ 
  row, 
  columns, 
  columnStyles, 
  rowHeight, 
  onRowClick,
  rowIndex 
}: any) => (
  <TableRow
    hover
    onClick={() => onRowClick?.(row)}
    style={{
      cursor: onRowClick ? 'pointer' : 'default',
      height: rowHeight,
    }}
  >
    {columns.map((column: any, colIndex: number) => {
      const value = row[column.id];
      return (
        <TableCell
          key={column.id}
          align={columnStyles[colIndex].align}
          style={{
            width: columnStyles[colIndex].width,
          }}
        >
          {column.format ? column.format(value) : value}
        </TableCell>
      );
    })}
  </TableRow>
));

VirtualizedTableRow.displayName = 'VirtualizedTableRow';

export default VirtualizedTable;
